import { Link, createFileRoute, notFound, redirect } from "@tanstack/react-router";

import { ProfileShell } from "../components/profile/ProfileShell";
import { BenchmarkStatCard } from "../components/profile/BenchmarkStat";
import { ErpChart } from "../components/profile/ErpChart";
import { CategoryPage } from "../components/profile/CategoryPage";
import { PROFILE_CATEGORIES } from "../data/wb/profile/categories";
import {
  POPULATION_HEADLINE,
  POPULATION_HIGHLIGHTS,
  ERP_TIMESERIES,
} from "../data/wb/profile/population-highlights";
import { DATASETS } from "../data/wb/profile/datasets";

// Legacy / shorthand slugs that should redirect to the canonical category URL.
const SLUG_ALIASES: Record<string, string> = {
  approvals: "building-approvals",
  "residential-approvals": "building-approvals",
  "building-approval": "building-approvals",
};

export const Route = createFileRoute("/profile/$slug")({
  loader: ({ params }) => {
    const alias = SLUG_ALIASES[params.slug];
    if (alias) {
      throw redirect({ to: "/profile/$slug", params: { slug: alias }, replace: true });
    }
    const category = PROFILE_CATEGORIES.find((c) => c.slug === params.slug);
    if (!category) throw notFound();
    return { category };
  },

  component: ProfilePage,
  notFoundComponent: () => (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: "var(--sp-lg) var(--sp-md)" }}>
      <h1 style={{ fontFamily: "var(--font-heading)", color: "var(--green)" }}>
        Profile category not found
      </h1>
      <p>
        <Link to="/profile" style={{ color: "var(--green-mid)" }}>
          ← Back to all profile categories
        </Link>
      </p>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main style={{ maxWidth: 720, margin: "0 auto", padding: "var(--sp-lg) var(--sp-md)" }}>
      <h1 style={{ fontFamily: "var(--font-heading)", color: "var(--green)" }}>
        Something went wrong
      </h1>
      <p style={{ color: "var(--ink-mid)" }}>{(error as Error)?.message ?? "Unknown error"}</p>
    </main>
  ),

  head: (ctx) => {
    const loaderData = (ctx as { loaderData?: { category?: { title?: string; summary?: string } } } | undefined)?.loaderData;
    const params = (ctx as { params?: { slug?: string } } | undefined)?.params;
    const cat =
      loaderData?.category ??
      (params?.slug ? PROFILE_CATEGORIES.find((c) => c.slug === params.slug) : undefined);
    const title = cat?.title
      ? `${cat.title} — Warren-Blackwood Community Profile`
      : "Profile — Warren-Blackwood Community Profile";
    return {
      meta: [
        { title },
        { name: "description", content: cat?.summary ?? "Community profile category." },
        { property: "og:title", content: title },
        { property: "og:description", content: cat?.summary ?? "Community profile category." },
      ],
    };
  },

});

function ProfilePage() {
  const { category } = Route.useLoaderData();

  if (category.slug === "population") return <PopulationPage />;

  const dataset = DATASETS[category.slug];
  if (dataset) return <CategoryPage eyebrow={category.title} dataset={dataset} />;

  // Tier 2 categories that aren't built yet still get a routable placeholder
  // so the index can link consistently and we don't 404 on an in-flight slug.
  return (
    <ProfileShell eyebrow="Coming soon" title={category.title} lede={category.summary}>
      <p style={{ color: "var(--ink-mid)" }}>
        This page hasn't been built yet. Source data is in the .id Warren-Blackwood profile;
        it will be transcribed in a later stage.
      </p>
    </ProfileShell>
  );
}

function PopulationPage() {
  const growthDelta =
    POPULATION_HEADLINE.erpGrowthPercent - POPULATION_HEADLINE.australiaGrowthPercent;
  const growthClause =
    growthDelta > 0
      ? `${growthDelta.toFixed(2)} points faster than Australia`
      : `${Math.abs(growthDelta).toFixed(2)} points slower than Australia`;

  return (
    <ProfileShell
      eyebrow="Population highlights"
      title={`${POPULATION_HEADLINE.erp2025.toLocaleString()} people, growing ${POPULATION_HEADLINE.erpGrowthPercent}% a year`}
      lede={`Warren-Blackwood added ${POPULATION_HEADLINE.erpGrowthFromPreviousYear.toLocaleString()} residents in the last twelve months — ${growthClause}. The numbers below are the .id headline indicators, benchmarked against Greater Perth, WA and Australia.`}
      source="WA Parliamentary Library / .id community profile, ABS Census 2021 and ERP 2025."
    >
      <section
        aria-labelledby="erp-heading"
        style={{
          background: "var(--white)",
          border: "1px solid var(--rule)",
          borderRadius: 8,
          padding: "var(--sp-md)",
          marginBottom: "var(--sp-lg)",
        }}
      >
        <h2
          id="erp-heading"
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "1.25rem",
            color: "var(--green)",
            margin: 0,
            marginBottom: "var(--sp-xs)",
          }}
        >
          Estimated Resident Population, 2006–2025
        </h2>
        <p
          style={{
            fontSize: "0.9rem",
            color: "var(--ink-mid)",
            margin: 0,
            marginBottom: "var(--sp-sm)",
          }}
        >
          A steady upward line — Warren-Blackwood has grown every single year since the ABS
          ERP series began, adding {(
            POPULATION_HEADLINE.erp2025 - ERP_TIMESERIES[0].value
          ).toLocaleString()}{" "}
          residents since 2006.
        </p>
        <ErpChart data={ERP_TIMESERIES} />
      </section>

      <section aria-labelledby="indicators-heading">
        <h2
          id="indicators-heading"
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "1.4rem",
            color: "var(--rivergum)",
            marginBottom: "var(--sp-sm)",
            borderBottom: "1px solid var(--light-border)",
            paddingBottom: "0.3rem",
          }}
        >
          Headline indicators
        </h2>
        <p
          style={{
            color: "var(--ink-mid)",
            fontSize: "0.9rem",
            marginBottom: "var(--sp-md)",
          }}
        >
          Each card shows the Warren-Blackwood value, the change since the previous Census, and
          three benchmarks.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "var(--sp-sm)",
          }}
        >
          {POPULATION_HIGHLIGHTS.map((stat) => (
            <BenchmarkStatCard key={stat.id} stat={stat} />
          ))}
        </div>
      </section>
    </ProfileShell>
  );
}
